import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { filterDates } from 'components/formElements/DateRange';
import { defineds, intialMessage, userMessage } from 'constants/ui.constants';

export type ReportKey =
	| 'beneficiaries_report'
	| 'balances_report'
	| 'activities_report'
	| 'account_management_report'
	| 'payment_report'
	| 'accounting_final_report'
	| 'payment_status_report'
	| 'fx_conversions_report'
	| 'beneficiary_payments_report'
	| 'payment_fees_report';
export type ReportFilterType = 'none' | 'date' | 'dateRange' | 'scope' | 'beneficiaryDateRange';
export type ReportFormat = 'pdf' | 'csv';
export type ReportLocale = 'he' | 'en';

export interface IReportCatalogItem {
	reportKey: ReportKey;
	displayName: string;
	description?: string;
	defaultFormat: ReportFormat;
	supportedFormats: ReportFormat[];
	filterSpec: {
		type: ReportFilterType;
	};
}

interface reportsState {
	loading: boolean;
	message: userMessage;
	startDate?: Date;
	endDate?: Date;
	catalog: IReportCatalogItem[];
	selectedReportKey: string;
}

const initialState: reportsState = {
	loading: false,
	message: intialMessage,
	startDate: defineds.startOfMonth,
	endDate: new Date(),
	catalog: [],
	selectedReportKey: '',
};

export const reportsSlice = createSlice({
	name: 'reports',
	initialState,
	reducers: {
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setReportStartDate: (state, action: PayloadAction<any>) => {
			state.startDate = new Date(action.payload);
		},
		setReportDates: (state, action: PayloadAction<filterDates>) => {
			state.startDate = action.payload.startDate;
			state.endDate = action.payload.endDate;
		},
		setCatalog: (state, action: PayloadAction<IReportCatalogItem[]>) => {
			state.catalog = action.payload;
		},
		setSelectedReportKey: (state, action: PayloadAction<string>) => {
			state.selectedReportKey = action.payload;
		},
		setMessage: (state, action: PayloadAction<userMessage>) => {
			state.message = action.payload;
		},
		resetReportsForm: (state) => {
			state.selectedReportKey = '';
			state.startDate = defineds.startOfMonth;
			state.endDate = new Date();
			state.message = intialMessage;
		},
	},
});

export const {
	setLoading,
	setReportStartDate,
	setReportDates,
	setCatalog,
	setSelectedReportKey,
	setMessage,
	resetReportsForm,
} = reportsSlice.actions;

const reportsSelector = (state) => state.reports;

export const isLoading = (state: any) => state.reports.loading;
export const getMessage = (state: any) => state.reports.message;
export const getReportDates = createSelector([reportsSelector], (state: any) => {
	return {
		startDate: state.startDate,
		endDate: state.endDate,
	};
});
export const getReportsCatalog = (state: any): IReportCatalogItem[] => state.reports.catalog;
export const getSelectedReportKey = (state: any): string => state.reports.selectedReportKey;
export const getSelectedReport = createSelector([reportsSelector], (state: any): IReportCatalogItem | null => {
	return state.catalog.find((item: IReportCatalogItem) => item.reportKey === state.selectedReportKey) || null;
});

export default reportsSlice.reducer;
